import React, {Dispatch, FC, SetStateAction, useEffect, useRef} from 'react';
import {message} from "antd";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {clearDeleteEventCalendar, deleteEventCalendar} from "../../../../../store/reducers/eventCalendarReducer/eventCalendarReducer";
import {CustomModal} from "../../../../../components/UI/AntCustom/CustomModal";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {defineMessages, useIntl} from "react-intl";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {IEventCalendar} from "../../../../../store/reducers/eventCalendarReducer/eventCalendarReducerTypes";
import {Row, Col} from "antd";

const EventCalendarDeleteModalMessages = defineMessages({
  title: {
    id: "eventCalendarDeleteModal.title",
    defaultMessage: "Delete Event",
  },
  confirmMessage: {
    id: "eventCalendarDeleteModal.confirmMessage",
    defaultMessage: "Are you sure you want to delete this event?",
  },
});

interface IEventCalendarDeleteModal {
  eventCalendar: IEventCalendar;
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
  onSuccess?: () => void; // Callback для закрытия модалки создания после успешного удаления
}

export const EventCalendarDeleteModal: FC<IEventCalendarDeleteModal> = ({eventCalendar, isModalVisible, setIsModalVisible, onSuccess}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const {
    isLoading,
    isSuccess: isDeleteSuccess,
    errorMessage,
  } = useAppSelector(state => state.eventCalendar.deleteEventCalendar);
  const lastDeletedEventCalendarId = useAppSelector(state => state.eventCalendar.lastDeletedEventCalendarId);

  // Сохраняем eventCalendarId текущего события для сравнения с lastDeletedEventCalendarId
  const currentEventCalendarIdRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      // Очищаем состояние при размонтировании компонента
      dispatch(clearDeleteEventCalendar());
    }
  }, [dispatch])

  useEffect(() => {
    if (isModalVisible) {
      // Сохраняем eventCalendarId текущего события
      currentEventCalendarIdRef.current = eventCalendar?.eventCalendarId || null;
      // Очищаем состояние при открытии модалки, чтобы избежать проблем с предыдущими запросами
      dispatch(clearDeleteEventCalendar());
    } else {
      // Сброс состояния при закрытии модалки
      dispatch(clearDeleteEventCalendar());
      currentEventCalendarIdRef.current = null;
    }
  }, [isModalVisible, dispatch, eventCalendar])

  useEffect(() => {
    // НОВАЯ НАДЕЖНАЯ ЛОГИКА:
    // Сравниваем lastDeletedEventCalendarId из Redux с currentEventCalendarIdRef
    // Если они совпадают, значит это успешное удаление именно нашего события
    
    // Игнорируем isDeleteSuccess, если это не наше удаление
    if (isDeleteSuccess && lastDeletedEventCalendarId !== currentEventCalendarIdRef.current) {
      return;
    }
    
    // Обработка успешного удаления
    if (isModalVisible && !isLoading && isDeleteSuccess && lastDeletedEventCalendarId === currentEventCalendarIdRef.current) {
      setIsModalVisible(false);
      dispatch(clearDeleteEventCalendar());
      currentEventCalendarIdRef.current = null;
      
      // Закрываем модалку создания и обновляем список событий
      if (onSuccess) {
        onSuccess();
      }
      return;
    }
    
    // Обработка ошибок
    if (isModalVisible && !isLoading && errorMessage) {
      message.error(errorMessage);
    }
  }, [isDeleteSuccess, errorMessage, isLoading, isModalVisible, setIsModalVisible, onSuccess, dispatch, lastDeletedEventCalendarId])

  const handleDelete = () => {
    dispatch(deleteEventCalendar({
      eventCalendarId: eventCalendar.eventCalendarId,
    }));
  }

  return (
      <CustomModal
      title={intl.formatMessage(EventCalendarDeleteModalMessages.title)}
      open={isModalVisible}
      onCancel={() => {
        dispatch(clearDeleteEventCalendar());
        setIsModalVisible(false);
      }}
      centered
      footer={null}
      width={500}
    >
      <div style={{marginBottom: 24}}>
        <p>{intl.formatMessage(EventCalendarDeleteModalMessages.confirmMessage)}</p>
        <p><strong>{eventCalendar.eventName}</strong></p>
      </div>

      <Row gutter={16}>
        <Col flex={1}>
          <CustomButton
            block
            type="default"
            onClick={() => {
              dispatch(clearDeleteEventCalendar());
              setIsModalVisible(false);
            }}
          >
            {intl.formatMessage(CommonButtonMessages.cancel)}
          </CustomButton>
        </Col>
        <Col flex={1}>
          <CustomButton
            block
            type="primary"
            danger
            onClick={handleDelete}
            loading={isLoading}
          >
            {intl.formatMessage(CommonButtonMessages.delete)}
          </CustomButton>
        </Col>
      </Row>
    </CustomModal>
  );
};

